import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { LoggerContext } from './loggerContext';

// Does any logger field currently hold the keyboard? (TM-18a)
//
// One consumer today: the rest-end resume in useRestAdvance, which must NOT
// yank the focus out of a field the athlete is typing into. Antenore's rule for
// the transition is "wandering is temporary, the rest ending brings you back to
// the work" — but tearing a caret out of a half-typed weight is the new bug we
// would be buying, so the resume waits for the field to let go.
//
// Why a signal and not a lookup: RN's TextInput.State.currentlyFocusedInput()
// is imperative, unobservable, and its react-native-web behaviour is not the
// native one — a hook that has to re-run when the answer changes cannot read it,
// and packages/ui is built RN-portable by default. Keyboard.addListener would
// have been worse than absent: it never fires for a hardware keyboard, i.e. it
// fails silently on exactly the surface the lift targets. The fields already
// announce focus and blur; this only remembers what they said.
//
// It rides the LoggerContext bag rather than a context of its own: the bag is
// already the way the logger subtree reaches state owned above the <Modal>, and
// this state has to live up there for the same LG-13 reason the cursor does —
// the hook that reads it (useRestAdvance) is instantiated in the LoggerScreen
// body.

export type FieldFocusSignal = {
  /** True while at least one logger field holds focus. */
  anyFieldFocused: boolean;
  onFieldFocus: (fieldId: string) => void;
  onFieldBlur: (fieldId: string) => void;
};

/** Owner side — called once, in the LoggerScreen body, beside the cursor. */
export function useFieldFocusSignal(): FieldFocusSignal {
  // A SET, not a counter: focus moves between fields as focus-B-then-blur-A as
  // often as the other way round, and a counter that goes negative once never
  // reads true again.
  const held = useRef<Set<string>>(new Set());
  const [anyFieldFocused, setAnyFieldFocused] = useState(false);

  const onFieldFocus = useCallback((fieldId: string) => {
    held.current.add(fieldId);
    setAnyFieldFocused(held.current.size > 0);
  }, []);
  const onFieldBlur = useCallback((fieldId: string) => {
    held.current.delete(fieldId);
    setAnyFieldFocused(held.current.size > 0);
  }, []);

  return useMemo(
    () => ({ anyFieldFocused, onFieldFocus, onFieldBlur }),
    [anyFieldFocused, onFieldFocus, onFieldBlur],
  );
}

/**
 * Leaf side — one per component; chain `onFocus(id)` / `onBlur(id)` onto each
 * field's own handlers.
 *
 * The id is passed at CALL time, not at hook time, so a panel that renders its
 * cells from a list (the cardio optionals) reports like any other — a hook per
 * cell is not available inside a map, and half a panel reporting is worse than
 * none: the resume would read "nobody is typing" and fire on the one branch
 * that was missed.
 *
 * `fieldId` only has to be unique among the fields mounted AT THE SAME TIME.
 *
 * ⚠️ Two things here are load-bearing, not tidiness:
 *
 *  - **The signal is read through a ref.** Depending on its identity is what
 *    breaks it: the owner rebuilds the signal object when anyFieldFocused
 *    flips, so an effect keyed on it re-runs — and its CLEANUP blurs the field
 *    that just took focus, inside the same flush. The signal then reads false
 *    exactly when it should read true, and nothing downstream ever sees the
 *    keyboard. (Found by the review, reproduced with a harness, and now pinned
 *    by fieldFocusSignal.test.tsx.)
 *  - **The unmount cleanup releases every id this component raised.** A focused
 *    field can be unmounted without ever blurring (the card swaps under it).
 *    Left in the set it would make anyFieldFocused true forever, and the resume
 *    would wait for a blur that can never come.
 */
export function useFieldFocusReporter(): {
  onFocus: (fieldId: string) => void;
  onBlur: (fieldId: string) => void;
} {
  // Tolerate the context's absence: these panels are also rendered outside the
  // provider (isolated tests), and a field that cannot report is a field that
  // simply never blocks the resume.
  const signal = useContext(LoggerContext)?.fieldFocus ?? null;
  const signalRef = useRef<FieldFocusSignal | null>(signal);
  signalRef.current = signal;
  const raised = useRef<Set<string>>(new Set());

  const onFocus = useCallback((fieldId: string) => {
    raised.current.add(fieldId);
    signalRef.current?.onFieldFocus(fieldId);
  }, []);
  const onBlur = useCallback((fieldId: string) => {
    raised.current.delete(fieldId);
    signalRef.current?.onFieldBlur(fieldId);
  }, []);

  useEffect(
    () => () => {
      for (const fieldId of raised.current) {
        signalRef.current?.onFieldBlur(fieldId);
      }
      raised.current.clear();
    },
    [],
  );

  return useMemo(() => ({ onFocus, onBlur }), [onFocus, onBlur]);
}
